import React, { useState } from "react";
import { resubscribe, openBillingPortal, redeemVoucher, startTrial } from "../api";
import PlanPicker from "./PlanPicker";
import { setCheckoutFlow, track } from "../utils/analytics";

/**
 * Shown to an authenticated user without app access. Two cases:
 *
 *  1. Never subscribed (no Subscription row) — e.g. an abandoned-signup account
 *     that bailed out of the original Stripe Checkout and was rescued at login.
 *     They still hold their one-time 7-day trial, so we offer to start it.
 *  2. Lapsed subscriber (trial ended without payment, payment failed, or
 *     canceled). Their account and data are preserved — only app access is gated.
 *
 * Recovery paths:
 *  - Start free trial / Resubscribe: a fresh Stripe Checkout. The backend grants
 *    the trial only to never-subscribed users; lapsed subscribers get no second
 *    trial. Both go through resubscribe().
 *  - Update payment: the Stripe Customer Portal. Primary for past_due/unpaid (fix card).
 */
function SubscriptionRequired({ user, onLogout }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showVoucher, setShowVoucher] = useState(false);
  const [voucher, setVoucher] = useState("");
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState(null);
  const [plan, setPlan] = useState("monthly"); // monthly | yearly (discounted, billed up front)

  const handleRedeemVoucher = async (e) => {
    e.preventDefault();
    if (!voucher.trim()) return;
    setVoucherError(null);
    setVoucherLoading(true);
    try {
      await redeemVoucher(voucher.trim());
      // Access granted — reload so /me is refetched and the app renders.
      window.location.assign("/");
    } catch (err) {
      setVoucherError(err.message || "Could not redeem that voucher.");
      setVoucherLoading(false);
    }
  };

  const status = user?.subscription?.status;
  // trial_available: never registered a real subscription (fresh signup, or an
  // abandoned one whose only row is a blank/incomplete stub) → offer the trial.
  const neverSubscribed = !!user?.trial_available;
  const paymentIssue = status === "past_due" || status === "unpaid";
  // Member of an institute that was disabled (or whose payment lapsed): offer
  // to continue as an individual subscriber — their data is kept.
  const instituteEnded =
    !!user?.institute &&
    user.institute.role !== "admin" &&
    (user.institute.status === "disabled" || user.institute.billing_ok === false);

  let headline;
  let blurb;
  if (instituteEnded) {
    headline = `${user.institute.name || "Your institute"}'s access has ended`;
    blurb = "Your institute's account is no longer active. You can keep all your progress and continue learning with your own subscription.";
  } else if (neverSubscribed) {
    headline = "Start your 7-day free trial";
    blurb = "You're all set up — start your free trial to begin practicing. You won't be charged until the trial ends, and you can cancel anytime.";
  } else if (paymentIssue) {
    headline = "Your payment didn't go through";
    blurb = "We couldn't process your latest payment. Update your payment details to restore access.";
  } else {
    // Covers both a just-ended free trial and a lapsed paid subscription.
    headline = "Your free access has ended";
    blurb = "Subscribe to keep practicing. Your account and progress are safe and waiting.";
  }

  // Actions that leave for Stripe return a { url }; the trial doesn't leave at
  // all, so a missing url means "done — reload and let /me re-decide".
  const go = async (fn, fallbackMsg) => {
    setError(null);
    setLoading(true);
    try {
      const { url } = (await fn()) || {};
      window.location.assign(url || "/");
    } catch (err) {
      setError(err.message || fallbackMsg);
      setLoading(false);
    }
  };

  const handleResubscribe = () => {
    setCheckoutFlow("resubscribe");
    return go(() => resubscribe(plan), "Could not start checkout.");
  };

  // Starting the trial is ours to grant — no Stripe, no card. Only reachable by
  // an account that never got one (an abandoned signup rescued at login);
  // signup itself grants the trial inline.
  const handleStartTrial = () =>
    go(async () => {
      const result = await startTrial();
      track("trial-started");
      return result;
    }, "Could not start your free trial.");
  const handleManage = () => go(openBillingPortal, "Could not open the billing portal.");

  // One clear action per state: update the card for a payment failure (Stripe
  // retries the existing sub), start the trial for a never-subscribed account,
  // or start a fresh Checkout for a canceled one. An ended institute routes to
  // a fresh individual Checkout (the backend detaches them once it completes).
  let action;
  if (instituteEnded) {
    action = { label: "Continue with my own subscription", onClick: handleResubscribe };
  } else if (neverSubscribed) {
    action = { label: "Start free trial", onClick: handleStartTrial };
  } else if (paymentIssue) {
    action = { label: "Update payment details", onClick: handleManage };
  } else {
    action = { label: "Subscribe", onClick: handleResubscribe };
  }

  return (
    <div className="dark-scope relative min-h-screen flex items-center justify-center px-4 py-10 overflow-hidden" style={{ background: "var(--ink)" }}>
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-ink-100/50 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -left-24 w-96 h-96 rounded-full bg-gold-100/40 blur-3xl" />
      <div className="relative z-10 w-full max-w-sm animate-rise">
        <div className="text-center mb-7">
          <span className="text-6xl font-arabic-display text-ink-600 leading-none" dir="rtl">مراجعة</span>
          <div className="rule-ornament w-40 mx-auto mt-3"><span className="text-gold-400">✦</span></div>
        </div>
        <div className="bg-parchment-50 rounded-2xl shadow-soft-lg border border-parchment-300/70 p-8 text-center">
          <h2 className="text-2xl font-display font-semibold text-ink-800 mb-2">{headline}</h2>
          <p className="text-sm text-slate-500 mb-6">{blurb}</p>

          {error && (
            <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-4">{error}</p>
          )}

          {/* Only where someone is actually choosing to pay. Skipped for a
              payment issue (the subscription is kept — nothing to pick) and when
              starting the free trial, which costs nothing. */}
          {!paymentIssue && !neverSubscribed && (
            <PlanPicker plan={plan} onChange={setPlan} className="mb-4" />
          )}

          <button
            onClick={action.onClick}
            disabled={loading}
            className="w-full py-2.5 rounded-xl bg-ink-600 text-parchment-50 font-semibold text-sm hover:bg-ink-700 active:bg-ink-800 disabled:opacity-50 shadow-soft transition-colors"
          >
            {loading ? "Opening…" : action.label}
          </button>

          {/* Voucher redemption — an alternative to paying */}
          {!showVoucher ? (
            <button
              onClick={() => setShowVoucher(true)}
              className="mt-4 w-full py-2 text-sm text-ink-600 hover:text-ink-700 underline-offset-2 hover:underline"
            >
              Have a voucher code?
            </button>
          ) : (
            <form onSubmit={handleRedeemVoucher} className="mt-4 text-left">
              <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">Voucher code</label>
              <input
                type="text"
                value={voucher}
                onChange={(e) => setVoucher(e.target.value.toUpperCase())}
                placeholder="Enter your code"
                autoFocus
                className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base font-mono text-ink-800 placeholder:text-ink-300 placeholder:font-sans focus:outline-none focus:ring-2 focus:ring-ink-400 focus:border-ink-400 transition"
              />
              {voucherError && (
                <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mt-2">{voucherError}</p>
              )}
              <button
                type="submit"
                disabled={voucherLoading || !voucher.trim()}
                className="mt-3 w-full py-2.5 rounded-xl bg-gold-500 text-ink-900 font-semibold text-sm hover:bg-gold-400 disabled:opacity-50 shadow-soft transition-colors"
              >
                {voucherLoading ? "Redeeming…" : "Redeem voucher"}
              </button>
            </form>
          )}

          <button
            onClick={onLogout}
            className="mt-3 w-full py-2 text-slate-400 hover:text-slate-600 text-sm transition-colors"
          >
            Log out
          </button>
        </div>

        {/* The free tools stay free for every account, so a lapsed user must
            not be dead-ended here. These pages are mounted before App
            (index.js), so they render for them without any of the gates
            above; their launch switches only gate anonymous visitors. */}
        <div className="mt-5 text-center text-sm text-ink-300">
          <p>The free tools stay free for your account:</p>
          <p className="mt-1.5 flex flex-wrap justify-center gap-x-4 gap-y-1">
            {[
              ["/typing", "Typing tutor"],
              ["/irab", "Iʿrāb"],
              ["/sarf", "Conjugator"],
              ["/reader", "PDF reader"],
            ].map(([href, label]) => (
              <a
                key={href}
                href={href}
                onClick={() => track("free-tool-open-click", { context: "paywall", tool: href.slice(1) })}
                className="text-gold-400 hover:text-gold-300 underline underline-offset-2"
              >
                {label} →
              </a>
            ))}
          </p>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionRequired;
